import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Grid, Slide } from '@material-ui/core';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ScriptableContext,
    TooltipItem
} from 'chart.js';
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import BarChartIcon from '@material-ui/icons/BarChart';
import { getMonthName } from "../../util/DateHelper";

type OwnProps = {
    facilityId: number;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type FacilityAdmissionByMonthChartProps = OwnProps & StateProps & DispatchProps;

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const FacilityAdmissionByMonthChart = (props: FacilityAdmissionByMonthChartProps) => {
    const {
        PatientFacilityDrillDownStats,
        userPracticeCoordinatorProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestPatientLocationDrillDownStatsForProvider,
    } = props;

    const [tokenState, setTokenState] = useState<APIStore.TokenType | undefined>();

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (userPracticeCoordinatorProfile) {
            const { isErrored, notFound, isLoaded } = userPracticeCoordinatorProfile;
            if ((!isErrored && !notFound && !isLoaded) && tokenState?.accessToken) {
                requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);
            } else {
                if (isLoaded && tokenState?.accessToken) {
                    if (!PatientFacilityDrillDownStats.isLoaded && !PatientFacilityDrillDownStats.isLoading) {
                        requestPatientLocationDrillDownStatsForProvider(tokenState.accessToken, props.facilityId);
                    }
                }
            }
        }
    }, [tokenState, userPracticeCoordinatorProfile.coordinatorProfile]);

    if (!PatientFacilityDrillDownStats || PatientFacilityDrillDownStats.isLoading || !PatientFacilityDrillDownStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="right">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled" />
                    </Grid>
                </Grid>
            </Slide>
        );
    }

    if (PatientFacilityDrillDownStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>);
    }

    const admits: APIStore.AdmitSummaryStatistics[] =
        PatientFacilityDrillDownStats.patientFacilityDrillDownCounts?.admitsByMonth ?? [];

    let width: number, height: number, gradient: CanvasGradient;
    function getBlueGradient(ctx: any, chartArea: any) {
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        if (!gradient || width !== chartWidth || height !== chartHeight) {
            width = chartWidth;
            height = chartHeight;
            gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(0, "rgba(82, 189, 228, 0.05)");
            gradient.addColorStop(0.5, "rgba(82, 189, 228, 0.1)");
            gradient.addColorStop(1, "rgba(82, 189, 228, 0.2)");
        }
        return gradient;
    }

    const labels = admits.map(val => `${getMonthName(val.month)} ${val.year}`);
    const values = admits.map(val => val.totalAdmits);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
            },
            title: {
                display: true,
                text: "Admissions by Month",
            },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'line'>[]) => items[0]?.label ?? '',
                    label: (item: TooltipItem<'line'>) => ` Admissions: ${item.raw}`,
                },
            },
        },
    };

    const data = {
        labels,
        datasets: [
            {
                label: "Admits",
                data: values,
                borderColor: "rgba(82, 189, 228, 1)",
                backgroundColor: function (context: ScriptableContext<'line'>) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) {
                        return;
                    }
                    return getBlueGradient(ctx, chartArea);
                },
                fill: true,
            },
        ],
    };

    return (
        <React.Fragment>
            <Grid item xs={12} direction="column" container>
                <Grid item xs={12}>
                    <Line options={options} data={data} />
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(FacilityAdmissionByMonthChart as any);
